import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '../../../lib/dataSource'
import type { TaskPlacement } from '../../../lib/db/models'
import { rememberUndo } from '../../../lib/undo'
import { showInfoToast } from '../../../components/common/MessageToast'
import { setPendingSubtaskSelection } from '../utils/pendingSubtaskSelection'

/**
 * **Einhängen und Herauslösen per Tastatur und Menü** (#204, Etappe 4 und Unter-Plan Tastatur).
 * Dieselben Wege wie das Ziehen, nur ohne Geste: ⇧⌥↑/↓ und „Einhängen in …“ bzw. „Herauslösen“.
 *
 * Wie beim Ziehen gibt es keine Meldung, wenn es klappt, und `⌘Z` geht still. Eine Meldung
 * gibt es nur, wenn etwas nicht geht: Die Tastatur hat keinen Hinweis am Ziel wie die Maus.
 *
 * @param onExpandTask klappt das Ziel auf, damit man die neue Unteraufgabe dort sieht
 * @param onLeave nimmt die Zeilen aus der Liste — ihre Stelle fährt zu (`useTaskDragAndDrop`
 *                `leaveList`); gibt zurück, wie man es zurücknimmt
 */
export function useNestActions({ onExpandTask, onLeave }: {
  onExpandTask?: (taskId: string) => void
  onLeave?: (ids: readonly string[]) => () => void
} = {}) {
  const source = useDataSource()
  const { t } = useTranslation()

  /**
   * `at`: vorn oder hinten in die Checkliste (⇧⌥↓ kommt von oben, ⇧⌥↑ von unten); ohne Angabe hinten.
   * `select: false` — niemand zeigt das Ziel gleich (die Aufgaben-Details), also nichts vormerken.
   */
  const nest = useCallback(async (
    ids: string[],
    parentId: string,
    { select = true, at = 'end' }: { select?: boolean; at?: 'start' | 'end' } = {},
  ): Promise<boolean> => {
    if (ids.length === 0) return false
    const [target, ...moving] = await Promise.all([parentId, ...ids].map(id => source.getTask(id)))
    // Vorab, damit nichts zu- und wieder aufklappt: Wiederkehrend in wiederkehrend lehnt die Datenbank ab (#205).
    if (target?.recurrence != null && moving.some(task => task?.recurrence != null)) {
      showInfoToast(t('taskList.nest.refusedTitle'), { description: t('taskList.nest.recurringToast') })
      return false
    }
    // Vorgemerkt, bevor die Zeile erscheint: Die Checkliste holt sich den Wunsch ab.
    if (select) setPendingSubtaskSelection(ids)
    /*
      **Die alte Stelle fährt zu, während das Ziel aufklappt** — im selben Moment und vor dem
      Schreiben, wie beim Ziehen (gemeldet 28.09.2026). Klappte das Ziel erst danach auf, stand
      die Zeile voll in der Liste, bis die Datenbank sie herausnahm, und verschwand schlagartig;
      die aufklappende Karte sprang um eine Zeilenhöhe.
    */
    const undoLeave = onLeave?.(ids)
    onExpandTask?.(parentId)
    const outcome = await source.nestTasks(ids, parentId, at === 'start' ? null : undefined)
    if ('refused' in outcome) {
      undoLeave?.()
      setPendingSubtaskSelection(null)
      if (outcome.refused === 'recurring') showInfoToast(t('taskList.nest.refusedTitle'), { description: t('taskList.nest.recurringToast') })
      // Ein Ast, der unter der Aufgabe tiefer als vier Ebenen reichte (#181).
      if (outcome.refused === 'depth') showInfoToast(t('taskList.nest.refusedTitle'), { description: t('taskItem.subtaskTree.refused.depth') })
      return false
    }
    rememberUndo(
      () => source.restorePlacements(outcome.before),
      ids.length === 1
        ? t('undo.restored.nested', { title: moving[0]?.title ?? '' })
        : t('undo.restored.nestedMultiple', { count: ids.length }),
    )
    return true
  }, [source, t, onExpandTask, onLeave])

  /**
   * Löst Unteraufgaben heraus, direkt über (⇧⌥↑) oder unter (⇧⌥↓, Vorgabe) ihrer Aufgabe. Mehrere
   * behalten ihre Reihenfolge; `⌘Z` nimmt sie zusammen zurück.
   */
  const unnest = useCallback(async (ids: string[], where: 'above' | 'below' = 'below'): Promise<boolean> => {
    if (ids.length === 0) return false
    const first = await source.getTask(ids[0]!)
    // Jede landet direkt an der Aufgabe: Unter ihr schöbe die nächste die vorige nach unten —
    // deshalb dort von hinten nach vorn.
    const before: TaskPlacement[] = []
    for (const id of where === 'below' ? [...ids].reverse() : ids) {
      const placed = await source.unnestTask(id, where)
      if (placed) before.push(...placed)
    }
    if (before.length === 0) return false
    rememberUndo(
      () => source.restorePlacements(before),
      ids.length === 1
        ? t('undo.restored.unnested', { title: first?.title ?? '' })
        : t('undo.restored.unnestedMultiple', { count: ids.length }),
    )
    return true
  }, [source, t])

  return { nest, unnest }
}
